import { useEffect, useRef } from 'react';
import { Outlet } from 'react-router';
import { FormProvider, useForm } from 'react-hook-form';
import { useAppSelector } from '~/hooks/useAppSelector';
import { type IShiftDetailsFormValues } from './ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';

export const ShiftRequestLayout = () => {
  const userAssistedHygiene = useAppSelector(
    (state) => state.user.selectedChildOffice?.office_info?.isAssistedHygiene,
  );

  const methods = useForm<IShiftDetailsFormValues>({
    defaultValues: {
      dates: [],
      professionType: '',
      officeId: '',
      arrivalTime: '08:00 am',
      departureTime: '05:00 pm',
      // Default the meal break to 60 min / unpaid — ~90% of shifts use these,
      // saving most users a step.
      mealBreakDuration: '1 hr',
      isMealBreakPaid: false,
      rate: null,
      preferredSpecialty: '',
      isAssistedHygiene: userAssistedHygiene ?? undefined,
      procedures: [],
      invite: [],
      inviteNames: {},
      inviteSource: 'Shift Request',
      autoConfirm: { favorites: false, eligibleProfessionals: false },
      shiftOverrides: {},
    },
  });

  // Reset dependent fields when professionType changes
  const previousProfessionTypeRef = useRef(methods.getValues('professionType'));
  // Reset the rate recommendation when the office changes
  const previousOfficeIdRef = useRef(methods.getValues('officeId'));

  useEffect(() => {
    const subscription = methods.watch((values, { name }) => {
      if (
        name === 'professionType' &&
        values.professionType !== previousProfessionTypeRef.current
      ) {
        previousProfessionTypeRef.current = values.professionType || '';
        methods.setValue('rate', null);
        methods.setValue('preferredSpecialty', '');
        methods.setValue('procedures', []);
        methods.setValue('autoConfirm', { favorites: false, eligibleProfessionals: false });
      }

      // Reset the rate when the office changes so the recommendation reflects
      // the newly-selected office's state average instead of the previous
      // office's stale value.
      if (name === 'officeId' && values.officeId !== previousOfficeIdRef.current) {
        previousOfficeIdRef.current = values.officeId || '';
        methods.setValue('rate', null);
      }
    });
    return () => subscription.unsubscribe();
  }, [methods]);

  return (
    <FormProvider {...methods}>
      <Outlet />
    </FormProvider>
  );
};
